import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { Toast } from 'primereact/toast';
import type { RootState } from '@center-pwa/store/store';

const DeveloperMockSection: React.FC = () => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const toastRef = useRef<Toast>(null);

  const userId = useSelector((state: RootState) => state.userProfile?.userId);
  const identity = useSelector((state: RootState) => state.userProfile?.identity);
  const orgId = useMemo(() => {
    if (!identity?.users || !userId) return undefined;
    const currentUser = identity.users.find((u: any) => u.id === userId);
    if (!currentUser) return undefined;
    const org = Array.isArray(currentUser.organization)
      ? currentUser.organization[0]
      : currentUser.organization;
    return (org as any)?.id as string | undefined;
  }, [identity, userId]);

  useEffect(() => {
    if (!orgId) return;
    logAnalyticsEvent('org_settings_section_viewed', undefined, {
      sectionId: 'developer',
      orgId,
      isMocked: 'true',
    });
    logAnalyticsEvent('org_settings_mocks_section_clicked', undefined, {
      sectionId: 'developer',
      orgId,
    });
  }, [orgId, logAnalyticsEvent]);

  const handleComingSoon = useCallback(() => {
    toastRef.current?.show({
      severity: 'info',
      summary: t('orgSettings.placeholder.comingSoon'),
      life: 3000,
    });
  }, [t]);

  return (
    <div className="org-settings-section">
      <Toast ref={toastRef} />

      <div className="org-settings-section__demo-banner" role="status" aria-live="polite">
        <i className="pi pi-info-circle" aria-hidden="true" />
        <span>{t('orgSettings.demoBanner')}</span>
      </div>

      <header className="org-settings-section__header">
        <h2 className="org-settings-section__title">{t('orgSettings.sections.developer')}</h2>
      </header>

      <section className="org-settings-section__subsection">
        <h3 className="org-settings-section__subtitle">
          {t('orgSettings.developer.apiKeysTitle')}
        </h3>
        <div className="org-settings-section__empty">
          <i className="pi pi-key" aria-hidden="true" />
          <p>{t('orgSettings.placeholder.comingSoon')}</p>
          <button
            type="button"
            className="org-settings-section__cta-button"
            onClick={handleComingSoon}
          >
            {t('orgSettings.developer.createKey')}
          </button>
        </div>
      </section>

      <section className="org-settings-section__subsection">
        <h3 className="org-settings-section__subtitle">
          {t('orgSettings.developer.webhooksTitle')}
        </h3>
        <div className="org-settings-section__empty">
          <i className="pi pi-share-alt" aria-hidden="true" />
          <p>{t('orgSettings.placeholder.comingSoon')}</p>
          <button
            type="button"
            className="org-settings-section__cta-button"
            onClick={handleComingSoon}
          >
            {t('orgSettings.developer.addWebhook')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default DeveloperMockSection;
